import { Form, FormControl, FormItem, FormMessage } from "@/components/ui/form";
import { FormField } from "@/components/ui/form/FormField";
import { Input } from "@/components/ui/input";
import { t } from "i18next";
import type { UseFormReturn } from "react-hook-form";
import { ReadOnlyInputEdit } from "./ReadOnlyInputEdit";
import type { AccountSettingSchemaType } from "@/schemas/accountSettings.schema";
import { Country, State } from "country-state-city";
import { CountryCombobox } from "./CountryCombobox";
import { StateCombobox } from "./StateCombobox";
import { CityCombobox } from "./CityCombobox";
import { InfoDialog } from "@/components/ui/InfoDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Occupation } from "@/common/types/occupation";

interface Props {
  onSubmit: (data: Partial<AccountSettingSchemaType>) => void;
  form: UseFormReturn<AccountSettingSchemaType>;
  occupations: Occupation[];
  loadingOccupations: boolean;
}

export const ProfileForm = (props: Props): JSX.Element => {
  const onSingleFieldSubmit = (fieldName: keyof AccountSettingSchemaType) => {
    return (data: AccountSettingSchemaType): void => {
      const singleFieldData = { [fieldName]: data[fieldName] };
      props.onSubmit(singleFieldData);
    };
  };

  return (
    <Form {...props.form}>
      <form className="space-y-4 mt-2 ">
        <FormField
          control={props.form.control}
          name="firstname"
          render={({ field }) => (
            <InfoDialog
              title={`${t("settings.profile.editModal")} ${t("settings.profile.firstName")}`}
              trigger={
                <ReadOnlyInputEdit
                  label={t("settings.profile.firstName")}
                  value={field.value}
                />
              }
              onConfirm={props.form.handleSubmit(
                onSingleFieldSubmit(field.name)
              )}
            >
              <FormItem>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            </InfoDialog>
          )}
        />
        <FormField
          control={props.form.control}
          name="lastname"
          render={({ field }) => (
            <InfoDialog
              title={`${t("settings.profile.editModal")} ${t("settings.profile.lastName")}`}
              trigger={
                <ReadOnlyInputEdit
                  label={t("settings.profile.lastName")}
                  value={field.value}
                />
              }
              onConfirm={props.form.handleSubmit(
                onSingleFieldSubmit(field.name)
              )}
            >
              <FormItem>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            </InfoDialog>
          )}
        />
        <FormField
          control={props.form.control}
          name="phone"
          render={({ field }) => (
            <InfoDialog
              title={`${t("settings.profile.editModal")} ${t("settings.profile.phone")}`}
              trigger={
                <ReadOnlyInputEdit
                  label={t("settings.profile.phone")}
                  value={field.value}
                />
              }
              onConfirm={props.form.handleSubmit(
                onSingleFieldSubmit(field.name)
              )}
            >
              <FormItem>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            </InfoDialog>
          )}
        />
        <FormField
          control={props.form.control}
          name="occupation"
          render={({ field }) => (
            <InfoDialog
              title={`${t("settings.profile.editModal")} ${t("settings.profile.occupation")}`}
              trigger={
                <ReadOnlyInputEdit
                  label={t("settings.profile.occupation")}
                  value={field.value}
                />
              }
              onConfirm={props.form.handleSubmit(
                onSingleFieldSubmit("occupationId")
              )}
            >
              <FormItem>
                <Select
                  disabled={props.loadingOccupations}
                  value={field.value}
                  onValueChange={(value) => {
                    const selected = props.occupations.find(
                      (occ) => occ.occupation === value
                    );
                    if (selected) {
                      props.form.setValue("occupation", value);
                      props.form.setValue("occupationId", selected.id);
                    }
                  }}
                >
                  <SelectTrigger id="occupation">
                    <SelectValue
                      placeholder={t("signUp.form.occupationPlaceholder")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {props.occupations.map((occ) => (
                      <SelectItem key={occ.id} value={occ.occupation}>
                        {occ.occupation}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage>
                  {props.form.formState.errors.occupation?.message}
                </FormMessage>
              </FormItem>
            </InfoDialog>
          )}
        />
        <FormField
          control={props.form.control}
          name="country"
          render={({ field }) => (
            <InfoDialog
              title={`${t("settings.profile.editModal")} ${t("settings.profile.country")}`}
              trigger={
                <ReadOnlyInputEdit
                  label={t("settings.profile.country")}
                  value={Country.getCountryByCode(field.value)?.name ?? ""}
                />
              }
              onConfirm={props.form.handleSubmit(
                onSingleFieldSubmit(field.name)
              )}
            >
              <FormItem>
                <FormControl>
                  <CountryCombobox
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value.value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            </InfoDialog>
          )}
        />
        <FormField
          control={props.form.control}
          name="state"
          render={({ field }) => (
            <InfoDialog
              title={`${t("settings.profile.editModal")} ${t("settings.profile.state")}`}
              trigger={
                <ReadOnlyInputEdit
                  label={t("settings.profile.state")}
                  value={
                    State.getStateByCodeAndCountry(
                      field.value,
                      props.form.getValues().country
                    )?.name ?? ""
                  }
                />
              }
              onConfirm={props.form.handleSubmit(
                onSingleFieldSubmit(field.name)
              )}
            >
              <FormItem>
                <FormControl>
                  <StateCombobox
                    countryCode={props.form.getValues().country}
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value.value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            </InfoDialog>
          )}
        />
        <FormField
          control={props.form.control}
          name="city"
          render={({ field }) => (
            <InfoDialog
              title={`${t("settings.profile.editModal")} ${t("settings.profile.city")}`}
              trigger={
                <ReadOnlyInputEdit
                  label={t("settings.profile.city")}
                  value={field.value}
                />
              }
              onConfirm={props.form.handleSubmit(
                onSingleFieldSubmit(field.name)
              )}
            >
              <FormItem>
                <FormControl>
                  <CityCombobox
                    countryCode={props.form.getValues().country}
                    stateCode={props.form.getValues().state}
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value.value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            </InfoDialog>
          )}
        />
      </form>
    </Form>
  );
};
